import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Link, useHistory } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import Card from "../../components/Card";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import {
  createCampaign,
  deleteCampaign,
  dispatchCampaign,
  listCampaigns,
  previewCampaignAudience,
} from "../../api/campaigns.service";
import {
  CHANNELS,
  channelsOf,
  fmtDate,
  isDispatchable,
  isEditable,
  resolveTrans,
  segmentSummary,
  statusOf,
} from "./shared";
import QuickSend from "./QuickSend";
import styles from "./Campaigns.module.sass";

const TABS = [
  { value: "all", label: "Todas" },
  { value: "drafts", label: "Borradores" },
  { value: "sent", label: "Enviadas" },
];

const CHANNEL_FILTERS = [
  { value: "all", label: "Todos" },
  { value: "push", label: "Push" },
  { value: "mail", label: "Email" },
  { value: "inapp", label: "In-App" },
];

/** Columnas ordenables: clave → cómo extraer el valor comparable. */
const SORTERS = {
  name: (c) => (c.name || "").toLowerCase(),
  reached: (c) => c.stats?.sent || 0,
  status: (c) => c.status || "",
  sentAt: (c) => new Date(c.sentAt || 0).getTime(),
  createdAt: (c) => new Date(c.createdAt || 0).getTime(),
};

const tabOf = (c) => {
  if (c.legacy) return "sent";
  if (["draft", "scheduled", "cancelled"].includes(c.status)) return "drafts";
  return "sent";
};

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

const fmtSentDate = (c) => {
  const d = c.sentAt || (tabOf(c) === "sent" ? c.createdAt : null);
  return d ? fmtDate(d) : "—";
};

/**
 * Listado de campañas (solo ANUNCIOS: los replies de feedback viven en
 * Conversaciones). KPIs con los totales REALES (legacy incluido), tabla ordenada
 * por última enviada y reordenable pinchando en las cabeceras.
 */
const Campaigns = () => {
  const history = useHistory();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [sort, setSort] = useState({ key: "sentAt", dir: "desc" });

  const [quickOpen, setQuickOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toSend, setToSend] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const resp = await listCampaigns();
      setCampaigns(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudieron cargar las campañas" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!campaigns.some((c) => c.status === "sending")) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [campaigns]);

  const counts = useMemo(() => {
    const acc = { all: campaigns.length, drafts: 0, sent: 0 };
    campaigns.forEach((c) => {
      acc[tabOf(c)] += 1;
    });
    return acc;
  }, [campaigns]);

  // KPIs REALES: todas las campañas (legacy incluido).
  const kpis = useMemo(() => {
    const sent = campaigns.filter((c) => tabOf(c) === "sent");
    const delivered = campaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
    const failed = campaigns.reduce((sum, c) => sum + (c.stats?.failed || 0), 0);
    const last = sent.reduce((best, c) => {
      const d = new Date(c.sentAt || c.createdAt || 0).getTime();
      return d > (best?.time || 0) ? { time: d, campaign: c } : best;
    }, null);
    return [
      { icon: "promotion", color: "#CABDFF", label: "Campañas enviadas", value: fmtNum(sent.length) },
      { icon: "check-circle", color: "#B5E4CA", label: "Mensajes entregados", value: fmtNum(delivered) },
      { icon: "close-circle", color: "#FFBC99", label: "Fallidos", value: fmtNum(failed) },
      {
        icon: "clock",
        color: "#B1E5FC",
        label: "Última enviada",
        value: last ? fmtDate(new Date(last.time)) : "—",
      },
    ];
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = campaigns.filter((c) => {
      if (tab !== "all" && tabOf(c) !== tab) return false;
      if (channel !== "all" && !channelsOf(c).includes(channel)) return false;
      if (q) {
        const haystack = `${c.name || ""} ${resolveTrans(c.title, "es")} ${resolveTrans(
          c.title,
          "en"
        )}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    const getter = SORTERS[sort.key] || SORTERS.sentAt;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [campaigns, tab, channel, search, sort]);

  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" }
    );

  const sortHeader = (key, label, className) => (
    <button
      type="button"
      className={cn(styles.sortHeader, className, { [styles.sortActive]: sort.key === key })}
      onClick={() => toggleSort(key)}
    >
      {label}
      <span className={styles.sortArrow}>
        {sort.key === key ? (sort.dir === "desc" ? "▼" : "▲") : "▽"}
      </span>
    </button>
  );

  const openSendModal = async (campaign) => {
    setToSend({ campaign, targeted: null });
    try {
      const resp = await previewCampaignAudience(campaign._id);
      setToSend((prev) =>
        prev && prev.campaign._id === campaign._id
          ? { ...prev, targeted: resp?.data?.targeted ?? null }
          : prev
      );
    } catch (e) {
      /* el modal muestra "—" */
    }
  };

  const handleSend = async () => {
    if (!toSend) return;
    setBusy(true);
    try {
      const resp = await dispatchCampaign(toSend.campaign._id);
      if (resp?.data && typeof resp.data.targeted === "number") {
        setToast({ severity: "success", msg: `Encolado a ${fmtNum(resp.data.targeted)} destinatarios` });
        setToSend(null);
        load();
      } else {
        setToast({ severity: "error", msg: "No se pudo lanzar la campaña" });
      }
    } catch (e) {
      setToast({ severity: "error", msg: "Error al lanzar la campaña" });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      await deleteCampaign(toDelete._id);
      setToast({ severity: "success", msg: "Borrador eliminado" });
      setToDelete(null);
      load();
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo eliminar" });
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      const resp = await createCampaign({
        name: `${campaign.name} (copia)`,
        channels: channelsOf(campaign),
        category: campaign.category,
        segment: campaign.segment || {},
        title: campaign.title || [],
        body: campaign.body || [],
        pushBody: campaign.pushBody || undefined,
        linkUrl: campaign.linkUrl || undefined,
        linkTitle: campaign.linkTitle || undefined,
        webViewUrl: campaign.webViewUrl || undefined,
        imageUrl: campaign.imageUrl || undefined,
      });
      if (resp?.data?._id) history.push(`/campaigns/${resp.data._id}`);
      else setToast({ severity: "error", msg: "No se pudo duplicar" });
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo duplicar" });
    }
  };

  const openRow = (c) => {
    if (c.legacy) return;
    history.push(`/campaigns/${c._id}`);
  };

  return (
    <>
      <div className={styles.hero}>
        <div>
          <h1 className={styles.heroTitle}>Campañas</h1>
          <div className={styles.heroSub}>
            Anuncios por push, email e in-app. Las respuestas a feedback viven en Conversaciones.
          </div>
        </div>
        <div className={styles.heroActions}>
          <button className={cn("button-stroke", styles.quickButton)} onClick={() => setQuickOpen(true)}>
            ⚡ Anuncio rápido
          </button>
          <Link className={cn("button", styles.newButton)} to="/campaigns/new">
            <Icon name="add" size="20" />
            <span>Nueva campaña</span>
          </Link>
        </div>
      </div>

      <QuickSend visible={quickOpen} onClose={() => setQuickOpen(false)} onSent={load} onNotify={setToast} />

      <div className={styles.kpis}>
        {kpis.map((k) => (
          <div className={styles.kpi} key={k.label}>
            <div className={styles.kpiIcon} style={{ backgroundColor: k.color }}>
              <Icon name={k.icon} size="20" />
            </div>
            <div>
              <div className={styles.kpiValue}>{loading ? "…" : k.value}</div>
              <div className={styles.kpiLabel}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      <Card className={styles.card}>
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {TABS.map((t) => (
              <button
                key={t.value}
                className={cn(styles.tab, { [styles.tabActive]: tab === t.value })}
                onClick={() => setTab(t.value)}
              >
                {t.label}
                <span className={styles.tabCount}>{counts[t.value]}</span>
              </button>
            ))}
          </div>
          <div className={styles.tools}>
            <div className={styles.channelChips}>
              {CHANNEL_FILTERS.map((c) => (
                <button
                  key={c.value}
                  className={cn(styles.chip, { [styles.chipActive]: channel === c.value })}
                  onClick={() => setChannel(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className={styles.search}>
              <Icon name="search" size="20" />
              <input
                placeholder="Buscar campaña…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className={styles.skeletons}>
            {[...Array(5)].map((_, i) => (
              <div className={styles.skeletonRow} key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyEmoji}>📣</div>
            <div className={styles.emptyTitle}>
              {campaigns.length === 0 ? "Aún no hay campañas" : "Nada por aquí"}
            </div>
            <div className={styles.emptyText}>
              {campaigns.length === 0
                ? "Crea la primera: elige audiencia, canal y contenido."
                : "Prueba con otra pestaña, canal o búsqueda."}
            </div>
          </div>
        ) : (
          <div className={styles.list}>
            <div className={cn(styles.row, styles.rowHead)}>
              <div />
              {sortHeader("name", "Campaña")}
              {sortHeader("reached", "Alcanzados", styles.colAudience)}
              {sortHeader("status", "Estado", styles.colStatus)}
              {sortHeader("sentAt", "Enviada", styles.colDate)}
              {sortHeader("createdAt", "Creada", styles.colDate)}
              <div className={styles.colActions} />
            </div>
            {filtered.map((c) => {
              const chs = channelsOf(c);
              const status = statusOf(c);
              const stats = c.stats || {};
              const pct =
                stats.targeted > 0
                  ? Math.min(100, Math.round(((stats.sent || 0) / stats.targeted) * 100))
                  : null;
              return (
                <div
                  key={c._id || c.name}
                  className={cn(styles.row, {
                    [styles.rowLegacy]: c.legacy,
                    [styles.rowClickable]: !c.legacy,
                  })}
                  onClick={() => openRow(c)}
                >
                  <div className={styles.channelIcons}>
                    {chs.map((ch) => {
                      const m = CHANNELS[ch] || CHANNELS.push;
                      return (
                        <div
                          key={ch}
                          className={styles.channelIcon}
                          style={{ backgroundColor: m.color }}
                          title={m.label}
                        >
                          <Icon name={m.icon} size="20" />
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.main}>
                    <div className={styles.name}>
                      {c.name}
                      {c.legacy && <span className={styles.legacyBadge}>Histórico</span>}
                    </div>
                    <div className={styles.preview}>
                      <span className={styles.channelLabel}>
                        {chs.map((ch) => CHANNELS[ch]?.label || ch).join(" + ")}
                      </span>
                      {tabOf(c) === "drafts" ? ` · ${segmentSummary(c.segment)}` : ""}
                    </div>
                  </div>
                  <div className={cn(styles.colAudience, styles.reachedCell)}>
                    {stats.sent > 0 ? (
                      <>
                        <span className={styles.reachedNum}>{fmtNum(stats.sent)}</span>
                        {stats.targeted > stats.sent && (
                          <span className={styles.reachedOf}> / {fmtNum(stats.targeted)}</span>
                        )}
                        {pct != null && pct < 100 && (
                          <div className={styles.progressTrack}>
                            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                          </div>
                        )}
                        {stats.failed > 0 && (
                          <span className={styles.failedText}>{fmtNum(stats.failed)} err</span>
                        )}
                      </>
                    ) : (
                      <span className={styles.progressDash}>
                        {tabOf(c) === "drafts" ? segmentSummary(c.segment) : "—"}
                      </span>
                    )}
                  </div>
                  <div className={styles.colStatus}>
                    <span className={cn(status.className, styles.status)}>{status.label}</span>
                  </div>
                  <div className={cn(styles.colDate, styles.date)}>{fmtSentDate(c)}</div>
                  <div className={cn(styles.colDate, styles.date)}>{fmtDate(c.createdAt)}</div>
                  <div className={cn(styles.colActions, styles.actions)} onClick={(e) => e.stopPropagation()}>
                    {isDispatchable(c) && (
                      <button
                        className={styles.sendButton}
                        title={c.status === "sending" ? "Reintentar envío" : "Enviar ahora"}
                        onClick={() => openSendModal(c)}
                      >
                        <Icon name="promotion" size="16" />
                        {c.status === "sending" ? "Reintentar" : "Enviar"}
                      </button>
                    )}
                    {!c.legacy && (
                      <button
                        className={styles.action}
                        title={isEditable(c) ? "Editar" : "Ver detalle"}
                        onClick={() => openRow(c)}
                      >
                        <Icon name={isEditable(c) ? "edit" : "expand"} size="18" />
                      </button>
                    )}
                    {!c.legacy && (
                      <button className={styles.action} title="Duplicar" onClick={() => handleDuplicate(c)}>
                        <Icon name="copy" size="18" />
                      </button>
                    )}
                    {isEditable(c) && (
                      <button
                        className={cn(styles.action, styles.actionDanger)}
                        title="Eliminar"
                        onClick={() => setToDelete(c)}
                      >
                        <Icon name="trash" size="18" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Modal visible={!!toSend} onClose={() => (busy ? null : setToSend(null))}>
        {toSend && (
          <div className={styles.confirm}>
            <div className={cn("title-purple", styles.confirmTitle)}>Enviar campaña</div>
            <div className={styles.confirmBody}>
              <div className={styles.confirmName}>{toSend.campaign.name}</div>
              <div className={styles.confirmMeta}>
                <span>{channelsOf(toSend.campaign).map((ch) => CHANNELS[ch]?.label).join(" + ")}</span>
                <span>·</span>
                <span>{segmentSummary(toSend.campaign.segment)}</span>
              </div>
              <div className={styles.confirmCount}>
                {toSend.targeted == null ? (
                  "Calculando audiencia…"
                ) : (
                  <>
                    <strong>{fmtNum(toSend.targeted)}</strong> destinatarios
                  </>
                )}
              </div>
              <div className={styles.confirmWarn}>
                El envío se encola inmediatamente y no se puede deshacer.
              </div>
            </div>
            <div className={styles.confirmActions}>
              <button className="button-stroke" onClick={() => setToSend(null)} disabled={busy}>
                Cancelar
              </button>
              <button className="button" onClick={handleSend} disabled={busy}>
                {busy ? "Enviando…" : "Enviar ahora"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal visible={!!toDelete} onClose={() => (busy ? null : setToDelete(null))}>
        {toDelete && (
          <div className={styles.confirm}>
            <div className={cn("title-red", styles.confirmTitle)}>Eliminar borrador</div>
            <div className={styles.confirmBody}>
              <div className={styles.confirmName}>{toDelete.name}</div>
              <div className={styles.confirmWarn}>Se elimina definitivamente.</div>
            </div>
            <div className={styles.confirmActions}>
              <button className="button-stroke" onClick={() => setToDelete(null)} disabled={busy}>
                Cancelar
              </button>
              <button className={cn("button", styles.buttonDanger)} onClick={handleDelete} disabled={busy}>
                {busy ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default Campaigns;
